import React, { useEffect, useState } from "react";
import { api, apiErr, fmtMoney, fmtDate } from "../lib/api";
import { WHATSAPP_NUMBER } from "../lib/permissions";
import { PageHeader } from "../components/ResourcePage";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Eye, Trash2, MessageCircle } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  pending: "bg-amber-100 text-amber-700",
  confirmed: "bg-sky-100 text-sky-700",
  fulfilled: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-red-100 text-red-700",
};

export function Orders() {
  const [orders, setOrders] = useState([]);
  const [view, setView] = useState(null);
  const [delId, setDelId] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/orders"); setOrders(data); }
    catch (e) { toast.error(apiErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try { await api.put(`/orders/${id}`, { status }); toast.success("Order updated"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const doDelete = async () => {
    try { await api.delete(`/orders/${delId}`); toast.success("Deleted"); setDelId(null); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const waLink = (o) => `https://wa.me/${(o.customer_phone || "").replace(/\D/g, "").replace(/^0/, "234")}`;

  return (
    <div className="fade-up">
      <PageHeader title="Online Orders" subtitle="Orders placed by customers from the website shop." />

      <div className="rounded-lg border border-border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Order</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-28 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {orders.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="py-12 text-center text-muted-foreground">No online orders yet.</TableCell></TableRow>
            ) : orders.map((o) => (
              <TableRow key={o.id} data-testid="order-row">
                <TableCell className="font-semibold">{o.order_number}</TableCell>
                <TableCell className="text-sm">{fmtDate(o.date)}</TableCell>
                <TableCell className="text-sm">{o.customer_name}</TableCell>
                <TableCell className="text-sm">{o.customer_phone}</TableCell>
                <TableCell className="text-right num font-semibold">{fmtMoney(o.total)}</TableCell>
                <TableCell>
                  <Select value={o.status} onValueChange={(v) => setStatus(o.id, v)}>
                    <SelectTrigger className={`h-8 w-32 border-0 ${STATUS[o.status]}`} data-testid="order-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.keys(STATUS).map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setView(o)} data-testid="order-view"><Eye className="h-4 w-4" /></Button>
                    <a href={waLink(o)} target="_blank" rel="noreferrer"><Button variant="ghost" size="icon" className="h-8 w-8 text-emerald-600"><MessageCircle className="h-4 w-4" /></Button></a>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => setDelId(o.id)} data-testid="order-delete"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!view} onOpenChange={() => setView(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">{view?.order_number}</DialogTitle>
            <DialogDescription>Order details and items.</DialogDescription></DialogHeader>
          {view && (
            <div>
              <div className="space-y-0.5 text-sm">
                <p><span className="text-muted-foreground">Customer:</span> {view.customer_name}</p>
                <p><span className="text-muted-foreground">Phone:</span> {view.customer_phone}</p>
                <p><span className="text-muted-foreground">Address:</span> {view.customer_address || "—"}</p>
                {view.note && <p><span className="text-muted-foreground">Note:</span> {view.note}</p>}
                <p><span className="text-muted-foreground">Placed:</span> {fmtDate(view.date)}</p>
              </div>
              <table className="mt-4 w-full text-sm">
                <thead><tr className="border-b text-xs uppercase text-muted-foreground"><th className="py-1 text-left">Item</th><th className="text-center">Qty</th><th className="text-right">Amount</th></tr></thead>
                <tbody>
                  {view.items.map((i, x) => (
                    <tr key={x} className="border-b border-border/60"><td className="py-1.5">{i.name}</td><td className="text-center">{i.quantity}</td><td className="num text-right">{fmtMoney(i.quantity * i.unit_price)}</td></tr>
                  ))}
                </tbody>
              </table>
              <div className="mt-2 flex items-center justify-between border-t-2 border-primary pt-2">
                <span className="font-semibold">TOTAL</span>
                <span className="num font-display text-lg font-extrabold">{fmtMoney(view.total)}</span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delId} onOpenChange={() => setDelId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete this order?</AlertDialogTitle>
            <AlertDialogDescription>This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-destructive" data-testid="order-confirm-delete">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
